/**
 * Pure diff engine — Phase 4.
 * computeDiff(old, new) → DiffItem[]
 * Zero side-effects. No imports from supabase or React.
 */

import type {
  FormDefinition,
  FieldDefinition,
  ValidationRule,
  ConditionalRule,
  CalculatedField,
} from '../api/form-builder-api'

// ─── DiffItem type enum (closed set) ─────────────────────────────────────────

export type DiffItemType =
  // Field-level
  | 'field_added'
  | 'field_removed'
  | 'field_label_changed'
  | 'field_description_changed'
  | 'field_required_changed'
  | 'field_type_changed'
  | 'field_default_changed'
  // Options-level
  | 'option_added'
  | 'option_removed'
  | 'option_label_changed'
  | 'option_order_changed'
  // Validation-level
  | 'validation_added'
  | 'validation_removed'
  | 'validation_tightened'
  | 'validation_loosened'
  | 'validation_severity_changed'
  | 'validation_message_changed'
  // Conditional-level
  | 'conditional_added'
  | 'conditional_removed'
  | 'conditional_action_changed'
  | 'conditional_predicate_changed'
  // Calculated-level
  | 'calculated_added'
  | 'calculated_removed'
  | 'calculated_formula_changed'
  // Form-level metadata
  | 'form_title_changed'
  | 'form_description_changed'
  | 'form_required_for_final_submit_changed'
  | 'form_minimum_instances_changed'
  | 'form_instance_label_field_changed'
  | 'form_instance_type_changed'
  // Structure-level
  | 'field_reordered'

export interface DiffItem {
  type: DiffItemType
  fieldKey?: string
  before?: unknown
  after?: unknown
  /** Extra context depending on type (e.g. option key, validation id) */
  details?: Record<string, unknown>
}

// ─── Optional form-level metadata (not inside FormDefinition) ─────────────────

export interface FormDiffMeta {
  title?: string
  description?: string
  instanceType?: 'single' | 'repeatable'
  requiredForFinalSubmit?: boolean
  minimumInstances?: number
  instanceLabelFieldKey?: string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

type Opt = { option_key: string; label?: { fa?: string; en?: string }; display_order?: number }

function getOptions(field: FieldDefinition): Opt[] {
  return (field.type_config?.options as Opt[] | undefined) ?? []
}

function isSelectType(type: string): boolean {
  return type === 'single_select' || type === 'multi_select'
}

// Returns 'tightened' | 'loosened' | 'equivalent' | 'changed' for a validation pair
function compareValidationTightness(
  oldRule: ValidationRule,
  newRule: ValidationRule
): 'tightened' | 'loosened' | 'equivalent' | 'changed' {
  const o = oldRule.rule_config ?? {}
  const n = newRule.rule_config ?? {}
  const type = newRule.rule_type

  if (type === 'numeric_range' || type === 'physiological_range') {
    const oMin = o.min as number | undefined
    const oMax = o.max as number | undefined
    const nMin = n.min as number | undefined
    const nMax = n.max as number | undefined
    const tightened =
      (nMin !== undefined && oMin !== undefined && nMin > oMin) ||
      (nMax !== undefined && oMax !== undefined && nMax < oMax) ||
      (nMin !== undefined && oMin === undefined) ||
      (nMax !== undefined && oMax === undefined)
    const loosened =
      (nMin !== undefined && oMin !== undefined && nMin < oMin) ||
      (nMax !== undefined && oMax !== undefined && nMax > oMax) ||
      (nMin === undefined && oMin !== undefined) ||
      (nMax === undefined && oMax !== undefined)
    if (tightened && !loosened) return 'tightened'
    if (loosened && !tightened) return 'loosened'
    if (!tightened && !loosened) return 'equivalent'
    return 'changed'
  }

  if (type === 'min_length') {
    const oMin = o.min as number | undefined
    const nMin = n.min as number | undefined
    if (oMin === nMin) return 'equivalent'
    return (nMin ?? 0) > (oMin ?? 0) ? 'tightened' : 'loosened'
  }

  if (type === 'max_length') {
    const oMax = o.max as number | undefined
    const nMax = n.max as number | undefined
    if (oMax === nMax) return 'equivalent'
    return (nMax ?? Infinity) < (oMax ?? Infinity) ? 'tightened' : 'loosened'
  }

  if (type === 'max_decimal_places') {
    const oMax = o.max_decimals as number | undefined
    const nMax = n.max_decimals as number | undefined
    if (oMax === nMax) return 'equivalent'
    return (nMax ?? Infinity) < (oMax ?? Infinity) ? 'tightened' : 'loosened'
  }

  if (type === 'date_after_absolute' || type === 'date_after_relative') {
    // Later min date = tightened
    const oDate = o.date as string | undefined
    const nDate = n.date as string | undefined
    if (!oDate || !nDate || oDate === nDate) return 'equivalent'
    return nDate > oDate ? 'tightened' : 'loosened'
  }

  if (type === 'date_before_absolute' || type === 'date_before_relative') {
    // Earlier max date = tightened
    const oDate = o.date as string | undefined
    const nDate = n.date as string | undefined
    if (!oDate || !nDate || oDate === nDate) return 'equivalent'
    return nDate < oDate ? 'tightened' : 'loosened'
  }

  // regex_match, cross-field — treat non-equivalent change as 'changed'
  if (JSON.stringify(o) === JSON.stringify(n)) return 'equivalent'
  return 'changed'
}

// ─── Diff sub-functions ────────────────────────────────────────────────────────

function diffValidations(
  oldField: FieldDefinition,
  newField: FieldDefinition,
  items: DiffItem[]
): void {
  const key = newField.field_key
  const oldById = new Map(oldField.validations.map((v) => [v.rule_id, v]))
  const newById = new Map(newField.validations.map((v) => [v.rule_id, v]))

  for (const [id, oldRule] of oldById) {
    const newRule = newById.get(id)
    if (!newRule) {
      items.push({ type: 'validation_removed', fieldKey: key, details: { ruleId: id, ruleType: oldRule.rule_type, severity: oldRule.severity } })
      continue
    }
    // Severity changed
    if (oldRule.severity !== newRule.severity) {
      items.push({ type: 'validation_severity_changed', fieldKey: key, before: oldRule.severity, after: newRule.severity, details: { ruleId: id } })
    }
    // Tightening
    const tightness = compareValidationTightness(oldRule, newRule)
    if (tightness === 'tightened') {
      items.push({ type: 'validation_tightened', fieldKey: key, details: { ruleId: id, ruleType: newRule.rule_type } })
    } else if (tightness === 'loosened') {
      items.push({ type: 'validation_loosened', fieldKey: key, details: { ruleId: id, ruleType: newRule.rule_type } })
    }
    // Message changed
    if (JSON.stringify(oldRule.error_message) !== JSON.stringify(newRule.error_message)) {
      items.push({ type: 'validation_message_changed', fieldKey: key, details: { ruleId: id } })
    }
  }

  for (const [id, newRule] of newById) {
    if (!oldById.has(id)) {
      items.push({ type: 'validation_added', fieldKey: key, details: { ruleId: id, ruleType: newRule.rule_type, severity: newRule.severity } })
    }
  }
}

function diffOptions(
  oldField: FieldDefinition,
  newField: FieldDefinition,
  items: DiffItem[]
): void {
  const key = newField.field_key
  const oldOpts = getOptions(oldField)
  const newOpts = getOptions(newField)
  const oldByKey = new Map(oldOpts.map((o) => [o.option_key, o]))
  const newByKey = new Map(newOpts.map((o) => [o.option_key, o]))

  for (const [optKey, oldOpt] of oldByKey) {
    const newOpt = newByKey.get(optKey)
    if (!newOpt) {
      items.push({ type: 'option_removed', fieldKey: key, details: { optionKey: optKey, optionLabel: oldOpt.label?.fa } })
      continue
    }
    if (JSON.stringify(oldOpt.label) !== JSON.stringify(newOpt.label)) {
      items.push({ type: 'option_label_changed', fieldKey: key, before: oldOpt.label, after: newOpt.label, details: { optionKey: optKey } })
    }
  }

  for (const [optKey] of newByKey) {
    if (!oldByKey.has(optKey)) {
      items.push({ type: 'option_added', fieldKey: key, details: { optionKey: optKey } })
    }
  }

  // Order changed: compare option_key order for keys present in both
  const oldOrder = oldOpts.map((o) => o.option_key).filter((k) => newByKey.has(k))
  const newOrder = newOpts.map((o) => o.option_key).filter((k) => oldByKey.has(k))
  if (JSON.stringify(oldOrder) !== JSON.stringify(newOrder)) {
    items.push({ type: 'option_order_changed', fieldKey: key })
  }
}

function diffConditionals(
  oldRules: ConditionalRule[],
  newRules: ConditionalRule[],
  items: DiffItem[]
): void {
  const oldById = new Map(oldRules.map((r) => [r.rule_id, r]))
  const newById = new Map(newRules.map((r) => [r.rule_id, r]))

  for (const [id, oldRule] of oldById) {
    const newRule = newById.get(id)
    if (!newRule) {
      items.push({ type: 'conditional_removed', details: { ruleId: id, description: oldRule.description } })
      continue
    }
    if (JSON.stringify(oldRule.condition) !== JSON.stringify(newRule.condition)) {
      items.push({ type: 'conditional_predicate_changed', details: { ruleId: id, description: newRule.description } })
    }
    if (JSON.stringify(oldRule.actions) !== JSON.stringify(newRule.actions)) {
      const hasRequireAction = newRule.actions.some((a) => a.type === 'require')
      items.push({ type: 'conditional_action_changed', details: { ruleId: id, description: newRule.description, hasRequireAction } })
    }
  }

  for (const [id, newRule] of newById) {
    if (!oldById.has(id)) {
      const hasRequireAction = newRule.actions.some((a) => a.type === 'require')
      items.push({ type: 'conditional_added', details: { ruleId: id, description: newRule.description, hasRequireAction } })
    }
  }
}

function diffCalculated(
  oldCalcs: CalculatedField[],
  newCalcs: CalculatedField[],
  items: DiffItem[]
): void {
  const oldByKey = new Map(oldCalcs.map((c) => [c.field_key, c]))
  const newByKey = new Map(newCalcs.map((c) => [c.field_key, c]))

  for (const [key, oldC] of oldByKey) {
    const newC = newByKey.get(key)
    if (!newC) {
      items.push({ type: 'calculated_removed', fieldKey: key })
      continue
    }
    if (oldC.formula !== newC.formula) {
      items.push({ type: 'calculated_formula_changed', fieldKey: key, before: oldC.formula, after: newC.formula })
    }
  }

  for (const [key] of newByKey) {
    if (!oldByKey.has(key)) {
      items.push({ type: 'calculated_added', fieldKey: key })
    }
  }
}

function diffFormMeta(
  oldMeta: FormDiffMeta,
  newMeta: FormDiffMeta,
  items: DiffItem[]
): void {
  if (oldMeta.title !== newMeta.title) {
    items.push({ type: 'form_title_changed', before: oldMeta.title, after: newMeta.title })
  }
  if (oldMeta.description !== newMeta.description) {
    items.push({ type: 'form_description_changed', before: oldMeta.description, after: newMeta.description })
  }
  if (oldMeta.instanceType !== newMeta.instanceType) {
    items.push({ type: 'form_instance_type_changed', before: oldMeta.instanceType, after: newMeta.instanceType })
  }
  if (oldMeta.requiredForFinalSubmit !== newMeta.requiredForFinalSubmit) {
    items.push({ type: 'form_required_for_final_submit_changed', before: oldMeta.requiredForFinalSubmit, after: newMeta.requiredForFinalSubmit })
  }
  if (oldMeta.minimumInstances !== newMeta.minimumInstances) {
    items.push({ type: 'form_minimum_instances_changed', before: oldMeta.minimumInstances, after: newMeta.minimumInstances })
  }
  if (oldMeta.instanceLabelFieldKey !== newMeta.instanceLabelFieldKey) {
    items.push({ type: 'form_instance_label_field_changed', before: oldMeta.instanceLabelFieldKey, after: newMeta.instanceLabelFieldKey })
  }
}

// ─── Deterministic ID for anti-tamper comparison ─────────────────────────────

export function diffItemId(item: DiffItem): string {
  const optKey = (item.details?.optionKey as string | undefined) ?? ''
  const ruleId = (item.details?.ruleId as string | undefined) ?? ''
  return `${item.type}|${item.fieldKey ?? ''}|${optKey}|${ruleId}`
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function computeDiff(
  oldDef: FormDefinition,
  newDef: FormDefinition,
  oldMeta?: FormDiffMeta,
  newMeta?: FormDiffMeta
): DiffItem[] {
  const items: DiffItem[] = []

  const oldByKey = new Map(oldDef.fields.map((f) => [f.field_key, f]))
  const newByKey = new Map(newDef.fields.map((f) => [f.field_key, f]))

  // ── Removed fields ────────────────────────────────────────────────────────
  for (const [key, oldF] of oldByKey) {
    if (!newByKey.has(key)) {
      items.push({ type: 'field_removed', fieldKey: key, before: oldF.label })
    }
  }

  // ── Added / changed fields ────────────────────────────────────────────────
  for (const [key, newF] of newByKey) {
    const oldF = oldByKey.get(key)

    if (!oldF) {
      items.push({ type: 'field_added', fieldKey: key, after: newF.label, details: { isRequired: newF.is_required, fieldType: newF.field_type } })
      continue
    }

    if (JSON.stringify(oldF.label) !== JSON.stringify(newF.label)) {
      items.push({ type: 'field_label_changed', fieldKey: key, before: oldF.label, after: newF.label })
    }

    if (JSON.stringify(oldF.description) !== JSON.stringify(newF.description)) {
      items.push({ type: 'field_description_changed', fieldKey: key, before: oldF.description, after: newF.description })
    }

    if (oldF.is_required !== newF.is_required) {
      items.push({ type: 'field_required_changed', fieldKey: key, before: oldF.is_required, after: newF.is_required })
    }

    if (oldF.field_type !== newF.field_type) {
      items.push({ type: 'field_type_changed', fieldKey: key, before: oldF.field_type, after: newF.field_type })
    }

    if (JSON.stringify(oldF.default_value) !== JSON.stringify(newF.default_value)) {
      items.push({ type: 'field_default_changed', fieldKey: key, before: oldF.default_value, after: newF.default_value })
    }

    // Options (select fields)
    if (isSelectType(oldF.field_type) && isSelectType(newF.field_type)) {
      diffOptions(oldF, newF, items)
    }

    // Validations
    diffValidations(oldF, newF, items)
  }

  // ── Field reorder ─────────────────────────────────────────────────────────
  const oldOrder = oldDef.fields.map((f) => f.field_key)
  const newOrder = newDef.fields.map((f) => f.field_key).filter((k) => oldByKey.has(k))
  if (JSON.stringify(oldOrder.filter((k) => newByKey.has(k))) !== JSON.stringify(newOrder)) {
    items.push({ type: 'field_reordered' })
  }

  // ── Conditionals ──────────────────────────────────────────────────────────
  diffConditionals(oldDef.conditional_rules, newDef.conditional_rules, items)

  // ── Calculated fields ─────────────────────────────────────────────────────
  diffCalculated(oldDef.calculated_fields, newDef.calculated_fields, items)

  // ── Form-level metadata ────────────────────────────────────────────────────
  if (oldMeta && newMeta) {
    diffFormMeta(oldMeta, newMeta, items)
  }

  return items
}
